import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  IconButton,
  ListItemIcon,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Switch,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  FactCheck as FactCheckIcon,
  Assessment as AssessmentIcon,
  Storefront as StorefrontIcon,
  HelpOutline as HelpOutlineIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { useOrganizationStore } from '../stores/organization.store';
import { SetupCoachmark } from './selfSetup/SetupCoachmark';
import {
  HELP_GUIDE_APPEARANCE,
  HELP_GUIDE_CHANGE_LANGUAGE,
} from '../constants/knowledgeBaseTryIt';
import { helpI18n } from '../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';

const ACCOUNT_MENU_TRIGGER = 'data-account-menu-trigger';

/** Sidebar is rendered twice (mobile + desktop); pick the on-screen profile button. */
function findVisibleAccountMenuTrigger(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[${ACCOUNT_MENU_TRIGGER}]`);
  for (const node of nodes) {
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return node;
    }
  }
  return null;
}
import { useNotificationStore } from '../stores/notification.store';
import { OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { ZERO_NAV_CATALOG } from '../auth';
import type { ZeroNavGroup } from '../auth';
import oneSignalService from '../services/onesignal.service';
import OrgLogo from './organizations/OrgLogo';
import { Sidebar } from './Sidebar';
import { SelfSetupGate } from './selfSetup/SelfSetupGate';
import {
  SelfSetupNavItem,
  SelfSetupSidebarEffect,
} from './selfSetup/SelfSetupNavItem';
import { SelfSetupTourProvider } from './selfSetup/SelfSetupTourProvider';
import { APP_LANGUAGES, setAppLanguage, type AppLanguage } from '../i18n/app';
import { APP_FONT_FAMILY } from '../constants/fonts';
import { isZeroPilotPathAllowed } from '../auth/pilot';
import PilotLockedView from './pilot/PilotLockedView';

const drawerWidth = 280;
const collapsedDrawerWidth = 76;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'zero.sidebarCollapsed';

// Language endonyms use the same unified multi-script web-font stack as the rest
// of the app so every script renders consistently regardless of the OS.
const LANGUAGE_FONT_FAMILY = APP_FONT_FAMILY;

interface LayoutProps {
  children: React.ReactNode;
  onThemeToggle: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, onThemeToggle }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [helpAccountGuide, setHelpAccountGuide] = useState<string | null>(null);
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { unreadCount } = useNotificationStore();

  const effectiveDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleSidebarCollapseToggle = useCallback(
    () => setSidebarCollapsed((prev) => !prev),
    [],
  );

  const contentTransition = theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout,
    user,
    accessibleOrgIds,
    isPlatformAdmin: platformAdmin,
    tokenPayload,
  } = useAuthStore();
  const isPilot = tokenPayload?.is_pilot === true;
  const { organizations, getOrganizations, selectedOrganization, setSelectedOrganization } = useOrganizationStore();
  const { filterZeroNavItems } = useAuthPermissions();

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (
      guide !== HELP_GUIDE_CHANGE_LANGUAGE &&
      guide !== HELP_GUIDE_APPEARANCE
    ) {
      return;
    }
    setHelpAccountGuide(guide);
    markHelpGuideActive(guide);
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
    setSidebarCollapsed(false);
    if (isMobileViewport) {
      setMobileOpen(true);
    }
    // Wait for drawer/rail layout, then anchor to the visible bottom profile control.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const button = findVisibleAccountMenuTrigger();
        if (button) setAnchorEl(button);
      });
    });
  }, [location.search, location.pathname, navigate, isMobileViewport]);

  const finishAccountGuide = () => {
    setHelpAccountGuide(null);
    setAnchorEl(null);
    setLanguageMenuAnchor(null);
    clearHelpGuideActive();
  };

  const navIcons: Record<string, React.ReactNode> = {
    Dashboard: <DashboardIcon />,
    Organizations: <BusinessIcon />,
    Users: <PersonIcon />,
    Approvals: <FactCheckIcon />,
    Report: <AssessmentIcon />,
    "Branch analysis": <StorefrontIcon />,
  };

  const orgOptions = useMemo(() => {
    if (platformAdmin || accessibleOrgIds.length === 0) {
      return organizations;
    }
    const allowed = new Set(accessibleOrgIds);
    return organizations.filter((o) => allowed.has(o.id));
  }, [organizations, accessibleOrgIds, platformAdmin]);

  useEffect(() => {
    const savedOrg = sessionStorage.getItem('selectedOrganization');
    if (savedOrg) {
      try {
        const parsedOrg = JSON.parse(savedOrg) as OrganizationSchema;
        if (
          platformAdmin ||
          accessibleOrgIds.length === 0 ||
          accessibleOrgIds.includes(parsedOrg.id)
        ) {
          setSelectedOrganization(parsedOrg);
        } else {
          sessionStorage.removeItem('selectedOrganization');
        }
      } catch (error) {
        console.error('Error parsing saved organization:', error);
      }
    }
  }, [accessibleOrgIds, platformAdmin, setSelectedOrganization]);

  // After login (no saved org), pick the first accessible organization.
  useEffect(() => {
    if (selectedOrganization || orgOptions.length === 0) return;
    if (!platformAdmin && accessibleOrgIds.length === 0) return;

    const first = orgOptions[0];
    setSelectedOrganization(first);
    sessionStorage.setItem('selectedOrganization', JSON.stringify(first));
  }, [
    selectedOrganization,
    orgOptions,
    platformAdmin,
    accessibleOrgIds.length,
    setSelectedOrganization,
  ]);

  const fetchOrganizations = useCallback(async (page = 1, input = "") => {
    const params = { page, limit: 10, name: input || undefined };
    await getOrganizations(params);
  }, [getOrganizations]);

  useEffect(() => {
    fetchOrganizations(1, searchQuery);
  }, [fetchOrganizations, searchQuery]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    // Prefer the clicked control when it is on-screen (desktop rail / open mobile drawer).
    if (rect.width > 0 && rect.height > 0) {
      setAnchorEl(target);
      return;
    }
    setAnchorEl(findVisibleAccountMenuTrigger());
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    if (helpAccountGuide) {
      setHelpAccountGuide(null);
      clearHelpGuideActive();
    }
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageSelect = (language: AppLanguage) => {
    setAppLanguage(language);
    setLanguageMenuAnchor(null);
    setAnchorEl(null);
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      setHelpAccountGuide(null);
    }
  };

  // Sidebar nav labels are keyed off the catalog's English `text`/`group`.
  const navLabel = (text: string) => t(`nav.${text.toLowerCase()}`, text);
  const groupLabel = (group: ZeroNavGroup) =>
    t(`nav.groups.${group.toLowerCase()}`, group);

  const handleOrganizationChange = async (_: any, newValue: OrganizationSchema | null) => {
    if (newValue) {
      setSelectedOrganization(newValue);
      sessionStorage.setItem('selectedOrganization', JSON.stringify(newValue));
      
      // Register OneSignal player for the new organization
      if (!isPilot && user && oneSignalService.isReady()) {
        try {
          const userId = user.id ?? user.sub;
          if (userId) {
            await oneSignalService.registerPlayerWithBackend(newValue.id, userId);
            await oneSignalService.setExternalUserId(userId);
          }
        } catch (error) {
          console.error('Error registering OneSignal for organization:', error);
        }
      }
      
      // Force a reload of the current page to refresh the dashboard
      window.location.reload();
    }
  };

  // Register OneSignal when organization is selected
  useEffect(() => {
    if (isPilot) return;

    const registerOneSignalForOrganization = async () => {
      if (selectedOrganization && user && !user.data?.onesignal_player_id) {
        try {
          if (oneSignalService.isReady()) {
            const userId = user.id ?? user.sub;
            if (userId) {
              await oneSignalService.registerPlayerWithBackend(selectedOrganization.id, userId);
              await oneSignalService.setExternalUserId(userId);
            }
          }
        } catch (error) {
          console.error('Error registering OneSignal player:', error);
        }
      }
    };

    const timer = setTimeout(() => {
      registerOneSignalForOrganization();
    }, 2000);

    return () => clearTimeout(timer);
  }, [selectedOrganization, user, isPilot]);

  const visibleMenuItems = useMemo(() => {
    if (isPilot) {
      const pilotItems = selectedOrganization
        ? ZERO_NAV_CATALOG.filter((item) =>
            item.allowedTypes.includes(selectedOrganization.organization_type),
          )
        : ZERO_NAV_CATALOG;
      return pilotItems.map((item) => ({
        ...item,
        icon: navIcons[item.text] ?? <DashboardIcon />,
      }));
    }
    if (!selectedOrganization) {
      return ZERO_NAV_CATALOG.filter((item) => item.path !== '/users').map((item) => ({
        ...item,
        icon: navIcons[item.text] ?? <DashboardIcon />,
      }));
    }
    return filterZeroNavItems(
      ZERO_NAV_CATALOG,
      selectedOrganization.organization_type,
    ).map((item) => ({
      ...item,
      icon: navIcons[item.text] ?? <DashboardIcon />,
    }));
  }, [selectedOrganization, filterZeroNavItems, isPilot]);

  // Group visible items into collapsible sidebar sections, preserving catalog order.
  const menuGroups = useMemo(() => {
    const groups: { group: ZeroNavGroup; items: typeof visibleMenuItems }[] = [];
    for (const item of visibleMenuItems) {
      const bucket = groups.find((g) => g.group === item.group);
      if (bucket) {
        bucket.items.push(item);
      } else {
        groups.push({ group: item.group, items: [item] });
      }
    }
    return groups;
  }, [visibleMenuItems]);

  const logoSrc = new URL(
    theme.palette.mode === 'dark' ? '/images/zero_dark.png' : '/images/zero_light.png',
    import.meta.url,
  ).href;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Sidebar.Header
        collapsedChildren={
          <Box
            aria-label="Recyclee Zero"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Clip strictly to the logo's circular mark (the ring around "RE")
                  so the following letters never bleed in as an edge sliver. */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={logoSrc}
                  alt="Recyclee Zero"
                  sx={{
                    height: '28.75px',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'block',
                    ml: '-0.41px',
                    mt: '-0.13px',
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.66rem',
                letterSpacing: '0.06em',
                lineHeight: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Zero
            </Typography>
          </Box>
        }
      >
        <img
          src={logoSrc}
          alt="Recyclee Zero"
          style={{ height: 44, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Sidebar.Header>
      <Sidebar.ExpandedOnly>
        <Divider />
        <Box sx={{ px: 2, py: 2 }}>
          <Autocomplete
            value={selectedOrganization}
            onChange={handleOrganizationChange}
            onInputChange={(_, newInputValue) => {
              setSearchQuery(newInputValue);
            }}
            options={orgOptions}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('common.organization')}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${theme.palette.primary.main}40`,
                    },
                    '&:hover fieldset': {
                      borderColor: `${theme.palette.primary.main}80`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: `${theme.palette.primary.main}80`,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            )}
            fullWidth
          />
        </Box>
        <Divider />
      </Sidebar.ExpandedOnly>
      <Sidebar.Nav>
        {menuGroups.map(({ group, items }) => (
          <Sidebar.NavGroup key={group} label={groupLabel(group)}>
            {items.map((item) => (
              <SelfSetupNavItem
                key={item.text}
                item={{
                  path: item.path,
                  text: item.text,
                  icon: item.icon,
                  label: navLabel(item.text),
                  selected: location.pathname === item.path,
                  locked: isPilot && item.path !== '/',
                }}
                onNavigate={(path) => navigate(path)}
              />
            ))}
          </Sidebar.NavGroup>
        ))}
      </Sidebar.Nav>
      <Box sx={{ flexGrow: 1 }} />
      <Sidebar.ExpandedOnly>
        <Divider />
      </Sidebar.ExpandedOnly>
      <Sidebar.Nav>
        {selectedOrganization && (
          <Sidebar.NavItem
            icon={
              <Badge color="error" badgeContent={unreadCount} overlap="circular">
                <NotificationsIcon />
              </Badge>
            }
            label={t('nav.notifications')}
            selected={location.pathname === '/notifications'}
            onClick={() => navigate('/notifications')}
            locked={isPilot}
          />
        )}
        <Sidebar.NavItem
          icon={<HelpOutlineIcon />}
          label={t('nav.help')}
          selected={location.pathname.startsWith('/knowledge-base')}
          onClick={() => navigate('/knowledge-base')}
        />
        <Sidebar.NavItem
            icon={
              <OrgLogo
                name={selectedOrganization?.name ?? t('account.title')}
                logoUrl={selectedOrganization?.logo_url ?? null}
                size={28}
              />
            }
            label={user?.username ?? t('account.title')}
            selected={Boolean(anchorEl)}
            onClick={handleProfileMenuOpen}
            buttonProps={{ [ACCOUNT_MENU_TRIGGER]: 'true' }}
          />
      </Sidebar.Nav>
      <Sidebar.CollapseToggle onClick={handleSidebarCollapseToggle} />
    </Box>
  );

  return (
    <SelfSetupTourProvider>
    <SelfSetupSidebarEffect
      onExpandDesktop={() => setSidebarCollapsed(false)}
      onOpenMobile={() => {
        if (isMobileViewport) setMobileOpen(true);
      }}
    />
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: theme.palette.primary.main }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>
      <Sidebar
        width={drawerWidth}
        collapsedWidth={collapsedDrawerWidth}
        collapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
      >
        {drawer}
      </Sidebar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ display: { xs: 'flex', sm: 'none' } }} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: theme.palette.background.default,
            borderRadius: '16px',
            mt: { xs: 1.5, sm: 2 },
            mr: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
            ml: { xs: 1.5, sm: 0 },
          }}
        >
          {!isPilot && <SelfSetupGate />}
          {isPilot && !isZeroPilotPathAllowed(location.pathname) ? (
            <PilotLockedView />
          ) : (
            children
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        marginThreshold={8}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 220,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <OrgLogo
            name={selectedOrganization?.name ?? user?.username ?? 'Account'}
            logoUrl={selectedOrganization?.logo_url ?? null}
            size={36}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {user?.username}
            </Typography>
            {selectedOrganization && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {selectedOrganization.name}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <SetupCoachmark
          open={helpAccountGuide === HELP_GUIDE_APPEARANCE && Boolean(anchorEl)}
          title={tHelp('knowledgeBase.appearanceItemTitle')}
          subtitle={tHelp('knowledgeBase.appearanceItemSubtitle')}
          onDismiss={finishAccountGuide}
          fullWidth
          variant="menu"
        >
          <MenuItem
            id="help-account-appearance"
            onClick={() => {
              onThemeToggle();
              if (helpAccountGuide === HELP_GUIDE_APPEARANCE) {
                finishAccountGuide();
              }
            }}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
            }}
          >
            <ListItemIcon>
              {theme.palette.mode === 'dark' ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={t('account.darkMode')} />
            <Switch
              edge="end"
              size="small"
              checked={theme.palette.mode === 'dark'}
              onClick={(event) => event.stopPropagation()}
              onChange={() => {
                onThemeToggle();
                if (helpAccountGuide === HELP_GUIDE_APPEARANCE) {
                  finishAccountGuide();
                }
              }}
            />
          </MenuItem>
        </SetupCoachmark>
        <SetupCoachmark
          open={
            helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE && Boolean(anchorEl)
          }
          title={tHelp('knowledgeBase.changeLanguageItemTitle')}
          subtitle={tHelp('knowledgeBase.changeLanguageItemSubtitle')}
          onDismiss={finishAccountGuide}
          fullWidth
          variant="menu"
        >
          <MenuItem
            id="help-account-languages"
            onClick={handleLanguageMenuOpen}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
            }}
          >
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('account.languages')} />
            <ChevronRightIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          </MenuItem>
        </SetupCoachmark>
        <Divider />
        <MenuItem
          onClick={() => {
            void logout();
          }}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': { bgcolor: `${theme.palette.error.main}10` },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          {t('account.logout')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleLanguageMenuClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 240,
            maxHeight: 420,
            py: 0.5,
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            px: 2,
            py: 0.5,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          {t('account.languages')}
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {APP_LANGUAGES.map((language) => {
          const isActive = i18n.language.startsWith(language.code);
          const localizedName = t(
            `languages.${language.englishLabel.toLowerCase()}`,
            language.englishLabel,
          );
          const showSecondary = localizedName !== language.nativeLabel;
          return (
            <MenuItem
              key={language.code}
              selected={isActive}
              onClick={() => handleLanguageSelect(language.code)}
              dir="ltr"
              sx={{
                gap: 1.25,
                minHeight: 48,
                py: 0.75,
                px: 1.25,
                mx: 0.75,
                borderRadius: '10px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}10`,
                },
                '&.Mui-selected': {
                  bgcolor: `${theme.palette.primary.main}14`,
                  '&:hover': { bgcolor: `${theme.palette.primary.main}1F` },
                },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  bgcolor: isActive
                    ? theme.palette.primary.main
                    : theme.palette.action.hover,
                  color: isActive
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.secondary,
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {language.code.toUpperCase()}
              </Box>
              <ListItemText
                primary={language.nativeLabel}
                secondary={showSecondary ? localizedName : undefined}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: LANGUAGE_FONT_FAMILY,
                    fontSize: '0.95rem',
                    fontWeight: isActive ? 700 : 500,
                    lineHeight: 1.35,
                    textAlign: 'start',
                  },
                  dir: 'ltr',
                }}
                secondaryTypographyProps={{
                  sx: {
                    fontFamily: LANGUAGE_FONT_FAMILY,
                    fontSize: '0.72rem',
                    lineHeight: 1.3,
                    textAlign: 'start',
                  },
                  dir: 'ltr',
                }}
              />
              {isActive && (
                <CheckIcon
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
    </SelfSetupTourProvider>
  );
};

export default Layout;
